<template>
    <div
        v-if="!isStep7Complete"
        class="bg-white overflow-hidden shadow md:rounded-lg col-span-12 md:col-span-6 flex flex-col relative"
    >
        <div class="px-6 py-6">
            <div class="truncate">
                <div class="flex justify-between">
                    <div
                        class="text-lg font-medium text-slate-800 justify-between flex gap-2"
                    >
                        <icon icon="pass" />
                        Companion Pass
                    </div>
                </div>
            </div>
            <div class="md:mt-12">
                <div class="grid grid-cols-6 gap-4">
                    <div class="col-span-3 py-4 sm:px-4 grid divide-y gap-y-4">
                        <div class="">
                            <div
                                class="text-sm font-medium text-slate-500 mb-2"
                            >
                                {{ currentStep }} of 7 steps
                            </div>
                            <h3
                                class="text-xl font-semibold text-gray-900 mb-3"
                            >
                                {{ currentStepsContent.title }}
                            </h3>
                            <p class="text-sm">
                                {{ currentStepsContent.nextLine1 }}
                                <br />
                                {{ currentStepsContent.nextLine2 }}
                            </p>
                            <div
                                class="flex absolute top-25 left-10 right-0 sm:w-96 w-80 gap-2 mt-2"
                            >
                                <div
                                    v-for="(bar, bIndex) in steps.filter(
                                        (step) => step.id <= 7
                                    )"
                                    class="h-1 flex-grow bg-navy-200 rounded-lg w-16"
                                    :class="{ '!bg-orange': bar.complete }"
                                    :key="`bar-${bIndex}`"
                                ></div>
                            </div>
                            <a
                                href="/companionpass/"
                                class="btn-base btn-10x-orange !mt-16"
                                id="hs-dashboard-companion-pass"
                            >
                                View More
                            </a>
                        </div>
                    </div>
                    <div class="col-span-2 flex justify-end relative">
                        <div
                            class="absolute sm:bottom-[-1.5rem] sm:left-[-1.5rem] sm:w-[500px] w-[175px] left-[-1.5rem] bottom-[-1.5rem]"
                        >
                            <img
                                src="/images/companionpass/illustration.png"
                                alt="Southwest Companion Pass"
                                class="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <section-8-congratulations v-if="isStep7Complete || isStep8Visible" />
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import { useCompanionPassStore } from '@/store/companionpass';
import Icon from '@/components/ui/icon/icon.vue';
import Section8Congratulations from '@/components/companion-pass/tap-sections/section-8-congratulations.vue';

export default {
    props: [],
    components: { Icon, Section8Congratulations },
    computed: {
        ...mapState(useDashboardStore, ['loading']),
        ...mapState(useCompanionPassStore, ['steps', 'completeSteps']),
        stepsToComplete() {
            let count = 0;
            this.steps.forEach((bar) => {
                if (!bar.complete) {
                    count++;
                }
            });
            return count;
        },
        finStepNum() {
            if (8 in this.completeSteps) {
                return 8;
            }
            if (7 in this.completeSteps) {
                return 7;
            }
            if (6 in this.completeSteps) {
                return 6;
            }
            if (5 in this.completeSteps) {
                return 5;
            }
            if (4 in this.completeSteps) {
                return 4;
            }
            if (3 in this.completeSteps) {
                return 3;
            }
            if (2 in this.completeSteps) {
                return 2;
            }
            return 1;
        },
        nextStepNum() {
            return this.finStepNum;
        },
        nextNextStepNum() {
            if (this.finStepNum < 7) {
                return this.finStepNum + 1 + '.';
            } else {
                return '';
            }
        },
        currentStepsContent() {
            if (8 in this.completeSteps) {
                return this.stepsContent[8];
            }
            if (7 in this.completeSteps) {
                return this.stepsContent[7];
            }
            if (6 in this.completeSteps) {
                return this.stepsContent[6];
            }
            if (5 in this.completeSteps) {
                return this.stepsContent[5];
            }
            if (4 in this.completeSteps) {
                return this.stepsContent[4];
            }
            if (3 in this.completeSteps) {
                return this.stepsContent[3];
            }
            if (2 in this.completeSteps) {
                return this.stepsContent[2];
            }
            if (1 in this.completeSteps) {
                return this.stepsContent[1];
            }

            return this.stepsContent[0];
        },
        currentStep() {
            return this.finStepNum;
        },
        totalSteps() {
            return this.steps.length;
        },
        isStep7Complete() {
            return this.steps.find((step) => step.id === 7 && step.complete);
        },
        isStep8Visible() {
            return this.steps.find((step) => step.id === 8 && step.complete);
        },
    },
    data: () => ({
        stepsContent: [
            {
                title: 'Open 1st Southwest credit card',
                nextLine1:
                    'Your first step in earning your Companion Pass will be ',

                nextLine2: 'to open a Southwest Credit Card.',
            },
            {
                title: 'Finish adding card details to your My10x Wallet',
                nextLine1:
                    "You've added your first Southwest card to your wallet, but we need more info.",
                nextLine2: '',
            },
            {
                title: 'Wait at least 30 days',
                nextLine1:
                    'Some patience is required for this step - sit tight for 30 days before starting your next step.',
                nextLine2:
                    'Your next step in earning your Companion Pass will be to open a second Southwest Credit Card.',
            },
            {
                title: 'Open your second Southwest credit card',
                nextLine1:
                    'Your next step in earning your Companion Pass will be to open a second Southwest Credit Card.',
                nextLine2:
                    "After you've opened your 2nd Southwest Credit Card, add it to your My10x Wallet.",
            },
            {
                title: 'Finish adding card details to your My10x Wallet',
                nextLine1:
                    "You've added your second Southwest card to your wallet, but we need more info.",
                nextLine2: '',
            },
            {
                title: 'Wait until 1/1/2025',
                nextLine1:
                    "Sit back, relax, and whatever you do, don't meet the minimum spend on either card prior to 1/1/2025.",
                nextLine2:
                    "You're free and clear to meet the minimum spends now on both of your Southwest Credit Cards.",
            },
            {
                title: 'Meet minimum spends',
                nextLine1:
                    "You're free and clear to meet the minimum spends now on both of your Southwest Credit Cards.",
                nextLine2:
                    "You've done your part - now you just have to wait for your Companion Pass to post to your Southwest Rapid Rewards account.",
            },
            {
                title: 'Congratulations! ' + 'You’ve done your part',
                nextLine1:
                    'You’ve  met the criteria to earn Southwest companion pass - now you',
                nextLine2:
                    'just have to wait for your Companion pass to post to your southwest rapid rewards account.',
            },
        ],
    }),
    methods: {
        ...mapActions(useCompanionPassStore, [
            'getPassData',
            'getSteps',
            'updateCompleteSteps',
            'setPassEnabled',
            'togglePass',
        ]),
    },
    created() {},
    mounted() {
        this.getSteps();
        this.updateCompleteSteps();
    },
};
</script>
